import useDentalDataContext from 'src/packages/dental/hooks/useDentalContext';
import { Alert, Box, Button, Col, Collapse, Heading, Icon, Row, Spinner, Table, TableBody, TableData, TableRow, } from '@digitools/honest-react';
import React, { useState } from 'react';
import { getMemberEvent, tealiumFormat } from 'src/packages/dental/providers/DentalDataProvider';
import StatusBadge from 'src/components/Cards/StatusBadge';
import DentalText from 'src/packages/dental/constants/DentalText';
import { GetClaimDetails, GetDetailsPdf } from 'src/packages/dental/api/DentalApiCalls';
import useAuthentication from 'src/hooks/useAuthentication';
import useTranslate from 'src/hooks/useTranslate';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import { DentalSubHeading, ErrorHtml } from 'src/packages/dental/Dental';
import useMediaQuery from 'src/hooks/useMediaQuery';
import Card from 'src/components/Cards/Card';
import { Language } from 'src/types/Language';
import { getPdfFilename, saveFile } from 'src/utils/pdfUtil';
const UsersIcon = () => (<Icon name='profile' size='s' style={{ marginTop: '.26rem' }} color={'grayDarker'}/>);
const FileIcon = () => (<Icon name='documents' size='s' style={{ marginTop: '.26rem' }} color={'grayDarker'}/>);
const CollapsedIcon = () => (<Icon name='add' size='xs' className='d-flex align-items-center pr-2' color='primary'/>);
const ExpandedIcon = () => (<Icon name='subtract' size='xs' className='d-flex align-items-center pr-2' color='primary'/>);
const getStatusColor = (status) => {
    switch (status) {
        case 'CLOSED':
            return 'gray';
        case 'OPEN':
            return 'secondary';
        case 'READY_TO_ADJUDICATE':
            return 'success';
        case 'READY':
            return 'accent2Dark';
        default:
            return 'accent2Dark';
    }
};
const ClaimBoxes = () => {
    const { currentMember, tealiumFunction, subscriber } = useDentalDataContext();
    const { t } = useTranslate();
    const [pdfError, setPdfError] = useState('');
    const claimsLength = currentMember?.claims?.length || 0;
    const [claimsInfoLoading, setClaimsInfoLoading] = useState(Array(claimsLength).fill(false));
    const [pdfDetailsLoading, setPdfDetailsLoading] = useState(Array(claimsLength).fill(false));
    const { authHeader } = useAuthentication();
    const isMobile = useMediaQuery('(max-width: 1439px)');
    const isTitleView = useMediaQuery('(max-width: 780px)');
    const modifyArray = (array, index, boolean) => {
        return [...array.slice(0, index), boolean, ...array.slice(index, array.length)];
    };
    const addClaimDetails = async (member, claim, claimIndex) => {
        if (!claimsInfoLoading[claimIndex]) {
            setClaimsInfoLoading(modifyArray(claimsInfoLoading, claimIndex, true));
            //remove hardcoded language when we support Spanish
            GetClaimDetails(authHeader(), Language.ENGLISH, claim.claimNumber || '', member.id)
                .then(claimDetailsResponse => {
                claim.claimDetailsError = claimDetailsResponse.graphQlError;
                claim.claimDetails = claimDetailsResponse.claimDetails;
                tealiumFunction({
                    eventAction: claim.claimDetailsError ? TEALIUM_EVENT.EVENT_ACTION.ERROR : TEALIUM_EVENT.EVENT_ACTION.EXPAND,
                    eventName: tealiumFormat(claim.claimDetailsError ? claim.claimDetailsError : t(DentalText.claimDetails)),
                    eventStatus: claim.status?.code,
                    memberEvent: memberEvent,
                });
            })
                .finally(() => {
                setClaimsInfoLoading(modifyArray(claimsInfoLoading, claimIndex, false));
            });
        }
    };
    const memberEvent = getMemberEvent(currentMember, subscriber);
    const processDetailsPdf = (claimNumber, claimIndex) => {
        setPdfDetailsLoading(modifyArray(pdfDetailsLoading, claimIndex, true));
        //remove hardcoded language when we support Spanish
        GetDetailsPdf(Language.ENGLISH, authHeader(), currentMember?.id, claimNumber)
            .then((response) => {
            const fileName = getPdfFilename('Claims_', currentMember?.firstName || '', currentMember?.lastName || '', new Date());
            saveFile(response, fileName);
            tealiumFunction({
                eventType: 'claims',
                eventName: tealiumFormat(t(DentalText.downloadClaimDetailsText)),
                eventAction: TEALIUM_EVENT.EVENT_ACTION.DOWNLOAD,
                memberEvent: memberEvent,
            });
        })
            .catch(error => {
            setPdfError(t(DentalText.claimDetailsPdfError));
            tealiumFunction({
                eventAction: TEALIUM_EVENT.EVENT_ACTION.ERROR,
                eventName: tealiumFormat(t(DentalText.claimDetailsPdfError)),
                memberEvent: memberEvent,
            });
        })
            .finally(() => setPdfDetailsLoading(modifyArray(pdfDetailsLoading, claimIndex, false)));
    };
    const handleDetailsClick = (claimNumber, claimIndex) => {
        if (!pdfDetailsLoading[claimIndex] && !claimsInfoLoading[claimIndex]) {
            setPdfError('');
            processDetailsPdf(claimNumber, claimIndex);
        }
    };
    const handleSubClaimClick = (e, claim) => {
        if (e.target.parentElement.getAttribute('aria-expanded') == 'false' || e.target.getAttribute('aria-expanded') == 'false') {
            tealiumFunction({
                eventType: 'claims',
                eventStatus: claim.status?.code,
                eventName: tealiumFormat(t(DentalText.subClaim)),
                eventAction: TEALIUM_EVENT.EVENT_ACTION.EXPAND,
                memberEvent: memberEvent,
            });
        }
    };
    const handleViewDetailsClick = (e, claim) => {
        if (e.target.parentElement.getAttribute('aria-expanded') == 'false' || e.target.getAttribute('aria-expanded') == 'false') {
            if (claim.claimDetailsError) {
                tealiumFunction({
                    eventAction: TEALIUM_EVENT.EVENT_ACTION.ERROR,
                    eventName: tealiumFormat(claim.claimDetailsError),
                    memberEvent: memberEvent,
                });
            }
            else if (claim.claimDetails) {
                tealiumFunction({
                    eventType: 'claims',
                    eventStatus: claim.status?.code,
                    eventName: tealiumFormat(t(DentalText.claimDetails)),
                    eventAction: TEALIUM_EVENT.EVENT_ACTION.EXPAND,
                    memberEvent: memberEvent,
                });
            }
        }
    };
    return (<>
      <>
        {isMobile ? (<></>) : (<div className={'d-flex justify-content-between h-100 w-100'}>
            <DentalSubHeading isTitleView={isTitleView} className={'pb-1 mb-0 align-self-end'} text={t(DentalText.claimsTitle)}/>
          </div>)}
      </>
      {currentMember?.claims &&
            currentMember.claims?.map((claim, index) => (<>
            {claim.status && (<>
                <Card data-testid={'outer-card-claims' + index} borderColor={getStatusColor(claim.status?.code)} onClick={e => {
                        if (!claim.claimDetails && !claim.claimDetailsError) {
                            addClaimDetails(currentMember, claim, index);
                        }
                    }} heading={<div data-private>
                      <DentalSubHeading isTitleView={isTitleView} className={'p-0 m-0'} color={'secondaryDark'} text={claim.displayTitle}>
                      </DentalSubHeading>
                    </div>} secondaryHeading={<StatusBadge backgroundColor={getStatusColor(claim.status.code)}>{claim.status.value}</StatusBadge>}>
                  <div style={{ color: '#6b6b6b' }}>
                    <div className={'d-flex w-100 pb-3'}>
                      <UsersIcon />
                      <div style={{ paddingLeft: '.5rem' }} data-private>{t(DentalText.claimsForText) + claim.patientName}</div>
                    </div>

                    <div className={'d-flex w-100 pb-3'}>
                      <FileIcon />
                      <div style={{ paddingLeft: '.5rem' }}>{claim.claimNumberStr}</div>
                    </div>
                  </div>
                  {claim.claimNumber && (<Collapse headerElemType={isMobile ? 'h2' : 'h4'} headerElemStyle={isMobile ? 'h4' : 'h2'} className={'pb-3 p-0'} title={t(DentalText.claimDetails)} headerClick={e => handleViewDetailsClick(e, claim)} id={'claim' + index + 'Details'} expandedIcon={ExpandedIcon} collapsedIcon={CollapsedIcon} type={'alternate'} color={'primary'}>
                      {claimsInfoLoading[index] && (<Spinner aria-busy='true' aria-live='polite' data-testid='dental-spinner'/>)}
                      <>
                        {pdfError && (<>
                            <Row className={'pb-3'}>
                              <Col>
                                <Alert type={'error'}>{t(DentalText.claimDetailsPdfError)}</Alert>
                              </Col>
                            </Row>
                          </>)}

                        {claim?.claimDetails?.claimTreatmentGroups &&
                            claim?.claimDetails?.claimTreatmentGroups.map((group, index) => (<>
                              {claim.claimNumberStr && (group.treatmentGroupId || group.treatmentGroupId === '') && (<Collapse isOpen={claim?.claimDetails?.claimTreatmentGroups &&
                                        claim?.claimDetails?.claimTreatmentGroups?.length < 2} data-testid={claim.claimNumber + 'button'} style={{
                                        borderBottom: 0,
                                        paddingBottom: index + 1 == claim.claimDetails?.claimTreatmentGroups?.length ? '0' : '24px',
                                    }} title={claim.claimNumberStr + group.treatmentGroupId} headerClick={(e) => handleSubClaimClick(e, claim)}>
                                  <div className={'pl-0'}>
                                    {group.paymentStatements &&
                                        group.paymentStatements.map(statement => (<p id={'summaryMessageFor' + index}>
                                          <span>
                                            <Icon name='money' size='s' className={'pr-1'}/>
                                          </span>
                                          <span>{statement}</span>
                                        </p>))}
                                    <p className={'pl-0 pb-1'}>{group.cobSecondaryStatement}</p>
                                    {isMobile ? (<>
                                        {group.claimTreatments &&
                                            group.claimTreatments.map(service => (<div style={{ paddingBottom: '1.5rem' }} className={'pl-2 pr-2'}>
                                              <p data-private className={'pb-3'} style={{ fontSize: '18px' }}>
                                                {service.procedureName} ({service.procedureCode})
                                              </p>
                                              {claim?.claimDetails?.tableHeaders &&
                                                    claim?.claimDetails?.tableHeaders.map((headers, ind) => (<>
                                                    {claim?.claimDetails?.tableHeaders && headers.id && ind != 0 && (<Row className={'border-bottom pb-2 pl-2'}>
                                                        <Col>
                                                          <Heading elemType={'h4'}>
                                                            {claim?.claimDetails?.tableHeaders[ind]?.value}
                                                          </Heading>
                                                        </Col>
                                                        <Col>
                                                          <p data-private className={'text-right font-weight-light w-100'}>
                                                            {service[headers.id]}
                                                          </p>
                                                        </Col>
                                                      </Row>)}
                                                  </>))}
                                            </div>))}
                                      </>) : (<Table color={'white'} className={'pt-0 mt-0 mb-1'}>
                                        <TableBody>
                                          <TableRow className={'border-bottom pl-0 pr-0 w-100'} isHoverable={false}>
                                            {claim?.claimDetails?.tableHeaders &&
                                            claim?.claimDetails?.tableHeaders.map(header => (<TableData className={'border-0 pl-0 pr-0'}>
                                                  <Heading elemType={'h4'}>{header.value}</Heading>
                                                </TableData>))}
                                          </TableRow>

                                          {group.claimTreatments &&
                                            group.claimTreatments.map(service => (<TableRow className={'border-bottom pl-0 pr-0 w-100'} isHoverable={false}>
                                                <>
                                                  <TableData className={'border-0 pl-0 pr-0'} style={{ width: '25%' }}>
                                                    <Heading data-private elemType={'h4'}>
                                                      {service.procedureName} ({service.procedureCode})
                                                    </Heading>
                                                  </TableData>
                                                  {claim?.claimDetails?.tableHeaders &&
                                                    claim?.claimDetails?.tableHeaders.map((headers, ind) => (<>
                                                        {claim?.claimDetails?.tableHeaders &&
                                                            headers.id &&
                                                            ind != 0 && (<TableData data-private className={'border-0 font-weight-light pl-0 pr-0'}>
                                                              {service[headers.id]}
                                                            </TableData>)}
                                                      </>))}
                                                </>
                                              </TableRow>))}
                                          <TableRow className={'border-bottom pl-0 pr-0 w-100'} isHoverable={false} style={{ backgroundColor: '#F2F4F6' }}>
                                            <TableData className={'border-0 font-weight-light pl-0 pr-0'}></TableData>
                                            <TableData data-private className={'border-0 font-weight-light pl-0 pr-0'}>
                                              <p data-private>{group.totalDentistCharge}</p>
                                            </TableData>
                                            <TableData className={'border-0 font-weight-light pl-0 pr-0'}>
                                              <p data-private>{group.totalDeductible}</p>
                                            </TableData>
                                            <TableData className={'border-0 font-weight-light pl-0 pr-0'}>
                                              <p data-private>{group.totalCoinsurance}</p>
                                            </TableData>
                                            <TableData className={'border-0 font-weight-light pl-0 pr-0'}>
                                              <p data-private>{group.totalCoveredAmount}</p>
                                            </TableData>
                                            <TableData className={'border-0 font-weight-light pl-0 pr-0'}></TableData>
                                          </TableRow>
                                        </TableBody>
                                      </Table>)}
                                  </div>
                                  {isMobile && (<>
                                      {claim?.claimDetails?.tableHeaders &&
                                            claim?.claimDetails?.tableHeaders.length >= 5 && (<Box color={'grayLightest'} style={{ borderColor: 'lightgray', width: '101%' }}>
                                            <Row className={'border-bottom pb-2'}>
                                              <Col>
                                                <Heading elemType={'h4'}>
                                                  {claim?.claimDetails?.tableHeaders[1]?.value}
                                                </Heading>
                                              </Col>
                                              <Col>
                                                <p data-private className={'text-right font-weight-light w-100'}>
                                                  {group.totalDentistCharge}
                                                </p>
                                              </Col>
                                            </Row>
                                            <Row className={'border-bottom pb-2'}>
                                              <Col>
                                                <Heading elemType={'h4'}>
                                                  {claim?.claimDetails?.tableHeaders[2].value}
                                                </Heading>
                                              </Col>
                                              <Col>
                                                <p data-private className={'text-right font-weight-light w-100'}>
                                                  {group.totalDeductible}
                                                </p>
                                              </Col>
                                            </Row>
                                            <Row className={'border-bottom pb-2'}>
                                              <Col>
                                                <Heading elemType={'h4'}>
                                                  {claim?.claimDetails?.tableHeaders[3].value}
                                                </Heading>
                                              </Col>
                                              <Col>
                                                <p data-private className={'text-right font-weight-light w-100'}>
                                                  {group.totalCoinsurance}
                                                </p>
                                              </Col>
                                            </Row>
                                            <Row className={'border-bottom pb-2'}>
                                              <Col>
                                                <Heading elemType={'h4'}>
                                                  {claim?.claimDetails?.tableHeaders[4].value}
                                                </Heading>
                                              </Col>
                                              <Col>
                                                <p data-private className={'text-right font-weight-light w-100'}>
                                                  {group.totalCoveredAmount}
                                                </p>
                                              </Col>
                                            </Row>
                                          </Box>)}
                                    </>)}
                                </Collapse>)}
                            </>))}
                      </>
                      {claim?.claimDetailsError && (<ErrorHtml errorType={claim.claimDetailsError} generalErrorMessage={`${t(DentalText.claimDetailsGeneralError)}`} notFoundErrorMessage={t(DentalText.claimDetailsNotFoundError)}/>)}
                      <hr className={'mt-4 mb-2'}/>
                      {pdfDetailsLoading[index] ? (<Spinner data-testid='dental-spinner'/>) : (<Button className={`${pdfDetailsLoading[index] || claimsInfoLoading[index] ? 'text-info' : ''} pr-0 pt-2 pl-0`} style={{ paddingBottom: '2rem' }} data-testid={'claimDetailsPdfButton' + claim.claimNumber} onClick={() => {
                                handleDetailsClick(claim.claimNumber, index);
                            }}>
                          {<Icon name={'download'} color={pdfDetailsLoading[index] || claimsInfoLoading[index] ? 'gray' : 'secondaryLight'}></Icon>}
                          {' ' + t(DentalText.downloadClaimDetailsText)}
                        </Button>)}
                    </Collapse>)}
                </Card>
              </>)}
          </>))}
    </>);
};
export default ClaimBoxes;
